<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'

import Broadcast from './Broadcast.vue'
import Channel from './Channel.vue'
import { useMobileDialogBodyClass } from '../../../../composables/use-mobile-dialog-body-class'

import type { EpgWidgetParagraph } from '../../../../utils/paragraph-schema/paragraphs'

type EpgWidget = EpgWidgetParagraph['epgWidget']
type EpgResultSet = NonNullable<EpgWidget['initialBroadcasts']>[number]
type ChannelItem = EpgResultSet['channel']
type BroadcastItem = EpgResultSet['broadcasts'][number]
// header search results: broadcasts grouped by day, each carrying its channel
type EpgAutocompleteSearch = {
  channels: ChannelItem[],
  broadcasts: Record<string, (BroadcastItem & { channel: ChannelItem })[]>
}

const NO_RESULTS_DELAY_MS = 300

const props = defineProps<{
  searchPhrase?: string,
  epgAutocompleteSearch?: EpgAutocompleteSearch,
  hasApiError?: boolean,
  searchInProgress?: boolean
}>()

const emit = defineEmits<{
  'close-flyout': []
}>()

const flyoutRef = ref<HTMLElement | null>(null)
const noResultsDelayTimeout = ref<ReturnType<typeof setTimeout> | null>(null)
const delayedShowNoResults = ref(false)

const hasChannels = computed(() => !!props.epgAutocompleteSearch?.channels.length)
const hasBroadcasts = computed(() => Object.keys(props.epgAutocompleteSearch?.broadcasts ?? {}).length > 0)
const showNoResultsInfo = computed(() => delayedShowNoResults.value && !hasChannels.value && !hasBroadcasts.value && (props.searchPhrase?.length ?? 0) > 2 && !props.searchInProgress)
const showFlyout = computed(() => hasChannels.value || hasBroadcasts.value || showNoResultsInfo.value)

const formatDay = (date: string) => new Date(date).toLocaleDateString('de-DE', { weekday: 'long', day: '2-digit', month: '2-digit' })

// Click outside of the flyout to close it
const closeFlyout = (event: PointerEvent) => {
  const target = event.target as Node | null
  const input = flyoutRef.value?.closest('form')?.querySelector('input')
  const epgTab = document.querySelector('button[data-search-type="epg"]')
  if (flyoutRef.value?.contains(target) || epgTab?.contains(target) || event.pointerId === -1 || target === input) return
  emit('close-flyout')
}

useMobileDialogBodyClass(showFlyout)

watch(() => props.searchPhrase, () => {
  if (noResultsDelayTimeout.value) clearTimeout(noResultsDelayTimeout.value)
  delayedShowNoResults.value = false
  noResultsDelayTimeout.value = setTimeout(() => {
    delayedShowNoResults.value = true
  }, NO_RESULTS_DELAY_MS)
})

onMounted(() => {
  document.addEventListener('click', closeFlyout)
})

onBeforeUnmount(() => {
  document.removeEventListener('click', closeFlyout)
  if (noResultsDelayTimeout.value) clearTimeout(noResultsDelayTimeout.value)
})
</script>

<template>
  <div v-if="showFlyout"
       ref="flyoutRef"
       tabindex="-1"
       class="bx-normalize bx-typo--epg bx-epg-search bx-epg-search--autocomplete">
    <div v-if="hasChannels"
         class="bx-epg-search__autocomplete-channels">
      <span>
        <strong>Sender</strong>
      </span>
      <div v-for="channel in epgAutocompleteSearch?.channels"
           :key="channel.originId"
           class="bx-epg-search__row">
        <Channel :channel="channel" />
      </div>
    </div>
    <div v-if="hasBroadcasts"
         class="bx-epg-search__autocomplete-broadcasts">
      <span>
        <strong>Sendungen</strong>
      </span>
      <div v-for="( broadcasts, date ) in epgAutocompleteSearch?.broadcasts"
           :key="date">
        <span>
          {{ formatDay(date) }}
        </span>
        <div v-for="broadcast in broadcasts"
             :key="broadcast.originId"
             class="bx-epg-search__row">
          <Channel :channel="{...broadcast.channel, url: broadcast.url}" />
          <div>
            <Broadcast :broadcast="broadcast" />
          </div>
        </div>
      </div>
    </div>
    <span v-if="showNoResultsInfo || hasApiError"
          class="bx-typo--14px bx-typo--center">
      <p v-if="showNoResultsInfo && !hasApiError">
        <strong>Keine passenden Sender oder Sendungen gefunden.</strong><br>
        Probiere einen allgemeineren Suchbegriff.
      </p>
      <p v-if="hasApiError">
        <strong>Unsere Server brauchen kurz eine Pause…</strong><br>Bitte später nochmal versuchen.
      </p>
    </span>
  </div>
</template>
